/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable } from '@angular/core';
import { LiveDTO } from '@app/modules/live/dtos/live';

import * as signalR from '@microsoft/signalr';
import { HubConnection } from '@microsoft/signalr';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { LiveTrackerSettings } from '../interfaces/ILiveTrackerSettings';

import { IReaderStatusSummaryModel } from '../interfaces/reader-status-summary-model.interface';
import { ITagProperties } from '../interfaces/tag-properties.interface';
import { GlobalVariablesService } from './global-variables.service';

export enum SignalRState {
    offline,
    online,
    reconnecting,
}

@Injectable({ providedIn: 'root' })
export class SignalRService {
    public showAlertSnackbar: boolean = true;

    private systemHubConnection!: HubConnection;
    private locationHubConnection!: HubConnection;
    private deviceHubConnection!: HubConnection;
    private propertyHubConnection!: HubConnection;

    private liveLocationUpdatedBehavior = new ReplaySubject<LiveDTO.TagLocation>();
    public onLiveLocationUpdateRecevied = this.liveLocationUpdatedBehavior.asObservable();

    private locationChangedBehaivor = new ReplaySubject<LiveDTO.TagLocation>();
    public onLocationChanged = this.locationChangedBehaivor.asObservable();

    private tagPropertiesChangeBehavior = new ReplaySubject<ITagProperties[]>(null);
    public onTagPropertiesReceived = this.tagPropertiesChangeBehavior.asObservable();

    private readerStatus = new ReplaySubject<IReaderStatusSummaryModel>(1);
    public onReaderStatus = this.readerStatus.asObservable();

    private signalRStatus = new BehaviorSubject<SignalRState>(0);
    public onSignalRStatus = this.signalRStatus.asObservable();

    private indicatorBehavior = new BehaviorSubject(false);
    public onIndicatorChanged = this.indicatorBehavior.asObservable();

    private liveSystemSettingsBehavior = new BehaviorSubject<LiveTrackerSettings>(null);
    public onLiveSystemSettingsChanged = this.liveSystemSettingsBehavior.asObservable();

    constructor(public global: GlobalVariablesService) {

        this.onLiveSystemSettingsChanged.subscribe((settings: LiveTrackerSettings) => {
            if (!settings) { return; }
            Object.assign(this.global.tracker, settings);
        });

    }

    get signalRIp(): string {
        return this.global.resolveTrackerAddress();
    }

    
    public async disconnect(): Promise<void> {
        await this.systemHubConnection.stop()
            .catch(err =>
                console.log('error while closing the connection: ' + err)
            );
        await this.locationHubConnection.stop()
            .catch(err =>
                console.log('error while closing the connection: ' + err)
            );
        await this.deviceHubConnection.stop()
            .catch(err =>
                console.log('error while closing the connection: ' + err)
            );

        await this.propertyHubConnection.stop()
            .catch(err =>
                console.log('error while closing the connection: ' + err)
            );
    }

    public locationHubConnectionStart(): void {

        this.locationHubConnection = new signalR.HubConnectionBuilder()
            .configureLogging(signalR.LogLevel.Error)
            .withUrl(this.withServiceWorkerBypass(this.global.buildTrackerUrl('/location-hub')),
                {
                    logMessageContent: true,
                    skipNegotiation: true,
                    transport: signalR.HttpTransportType.WebSockets,
                })
            .withAutomaticReconnect([0, 1000, 5000, 5000])
            .build();

        this.locationHubConnection
            .start()
            .then(() => {
                this.signalRStatus.next(1);

                this.locationHubConnection.invoke('GetConnectionID').then((connectionId) => {

                    console.log('locationHubConnection-connectionId', connectionId);

                    if (connectionId !== null || connectionId !== undefined) {
                        this.locationHubConnection.invoke('AddToGroup', 'NavizardUser');
                    }

                });
            })
            .catch((err) => {
                this.signalRStatus.next(0);
                console.error(err.toString());
            });

        this.locationHubConnection.on('LocationUpdated', (data) => {

            // console.log('LocationUpdated', data);

            if (data) {
                this.liveLocationUpdatedBehavior.next(data);
            }

        });

        this.locationHubConnection.on('LocationChanged', (data) => {

            //  console.log('LocationChanged', data);

            if (data) {
                this.locationChangedBehaivor.next(data);
            }
        });

    }

    public deviceHubConnectionStart(): void {

        this.deviceHubConnection = new signalR.HubConnectionBuilder()
            .configureLogging(signalR.LogLevel.Error).withUrl(this.withServiceWorkerBypass(this.global.buildTrackerUrl('/device-hub')),
                {
                    logMessageContent: true,
                    skipNegotiation: true,
                    transport: signalR.HttpTransportType.WebSockets,
                })
            .withAutomaticReconnect([0, 1000, 5000, 5000])
            .build();

        this.deviceHubConnection
            .start()
            .then(() => {
                this.signalRStatus.next(1);

                this.deviceHubConnection.invoke('GetConnectionID').then((connectionId) => {

                    console.log('deviceHubConnection-connectionId', connectionId);

                });
            })
            .catch((err) => {
                this.signalRStatus.next(0);
                console.error(err.toString());
            });

        this.deviceHubConnection.on('ReaderStatusChanged', (data) => {

        });
    }

    public systemHubConnectionStart(): void {

        this.systemHubConnection = new signalR.HubConnectionBuilder()
            .configureLogging(signalR.LogLevel.Error).withUrl(this.withServiceWorkerBypass(this.global.buildTrackerUrl('/system-hub')),
                {
                    logMessageContent: true,
                    skipNegotiation: true,
                    transport: signalR.HttpTransportType.WebSockets,
                })
            .withAutomaticReconnect([0, 1000, 5000, 5000])
            .build();

        this.systemHubConnection
            .start()
            .then(() => {
                this.signalRStatus.next(1);

                this.systemHubConnection.invoke('GetConnectionID').then((connectionId) => {

                    console.log('systemHubConnection-connectionId', connectionId);

                    if (connectionId !== null || connectionId !== undefined) {
                        this.systemHubConnection.invoke('AddToGroup', 'NavizardUser');
                    }

                });
            })
            .catch((err) => {
                this.signalRStatus.next(0);
                console.error(err.toString());
            });

        this.systemHubConnection.on('Settings', (data: LiveTrackerSettings) => {
            if (data) {
                this.liveSystemSettingsBehavior.next(data);
            }
        });
    }

    public propertyHubConnectionStart(): void {

        this.propertyHubConnection = new signalR.HubConnectionBuilder()
            .configureLogging(signalR.LogLevel.Error).withUrl(this.withServiceWorkerBypass(this.global.buildTrackerUrl('/property-hub')),
                {
                    logMessageContent: true,
                    skipNegotiation: true,
                    transport: signalR.HttpTransportType.WebSockets,
                })
            .withAutomaticReconnect([0, 1000, 5000, 5000])
            .build();

        this.propertyHubConnection
            .start()
            .then(() => {
                this.signalRStatus.next(1);

                this.propertyHubConnection.invoke('GetConnectionID').then((connectionId) => {

                    console.log('propertyHubConnection-connectionId', connectionId);

                });
            })
            .catch((err) => {
                this.signalRStatus.next(0);
                console.error(err.toString());
            });

        this.propertyHubConnection.on('PropertyUpdated', (data: ITagProperties[]) => {

            // console.log('PropertyUpdated', data);

            if (data) {
                this.tagPropertiesChangeBehavior.next(data);
            }

        });

    }

    private withServiceWorkerBypass(url: string): string {
        if (!url || /(?:\?|&)ngsw-bypass(?:=|&|$)/i.test(url)) {
            return url;
        }

        return `${url}${url.includes('?') ? '&' : '?'}ngsw-bypass=true`;
    }

}
